import type { LocationCoordinates } from 'lib/queries/locations';

import { useState } from 'react';
import useGeolocationEvents from 'beautiful-react-hooks/useGeolocationEvents';

export type UseUserLocation = Partial<LocationCoordinates> & {
  isRetrieving: boolean;
  isSupported: boolean;
} & Pick<ReturnType<typeof useGeolocationEvents>, 'onError'>;

export function useUserLocation(): UseUserLocation {
  const [status, updateStatus] = useState<UseUserLocation>({
    isRetrieving: true,
    isSupported: true,
    onError: () => null,
  });

  if (typeof window === 'undefined') {
    return status;
  }

  const { isSupported, onChange, onError } = useGeolocationEvents({
    enableHighAccuracy: true,
    maximumAge: 1024,
  });

  if (!isSupported) {
    return status;
  }

  onChange(position =>
    updateStatus(prev => {
      const coords = {
        lat: position?.coords?.latitude,
        lng: position?.coords?.longitude,
      };

      if (position && prev?.lat !== coords.lat && prev?.lng !== coords.lng) {
        return { ...prev, ...coords, isRetrieving: false };
      }

      return prev;
    }),
  );

  onError(() => updateStatus(prev => ({ ...prev, isRetrieving: false })));

  return { isSupported, onError, ...status };
}
